import { ReactNode } from "react";
// eslint-disable-next-line no-restricted-imports
import { IntercomProvider as ReactIntercomProvider } from "react-use-intercom";

import { serverApi } from "api/api-server-actions";
import { useAppSelector } from "store";
import {
  selectCurrentOrg,
  selectCurrentUser,
  selectCurrentUserComputedRole,
} from "store/selectors";
import { usePromiseEffect } from "store/store-hooks";
import { reportApiFailure } from "utils/api-errors";
import { IntercomConfig } from "utils/intercom-types";
import { isIntercomEnabled } from "utils/waffle-flags";

export const IntercomProvider = ({ children }: { children: ReactNode }) => {
  const user = useAppSelector(selectCurrentUser);
  const org = useAppSelector(selectCurrentOrg);
  const computedRole = useAppSelector(selectCurrentUserComputedRole);

  const { data: intercomConfig } = usePromiseEffect(async () => {
    if (!isIntercomEnabled() || !user || !org) {
      return null;
    }

    try {
      const response = await serverApi.get<IntercomConfig>("/api/v1/intercom/data/");
      return response.data;
    } catch (error) {
      // Graceful fallback - just log the error and continue without Intercom
      reportApiFailure("Failed to fetch Intercom configuration", error);
      return null;
    }
  }, [user, org]);

  const hasConfig = intercomConfig != null;
  const customAttributes = {
    role: computedRole,
    org: org?.name,
    environment: window._env_.ENV,
    product: "Forum V",
  };

  return (
    <ReactIntercomProvider
      appId={intercomConfig?.app_id ?? ""}
      autoBoot={hasConfig}
      autoBootProps={
        hasConfig
          ? {
              userId: intercomConfig.user_id,
              intercomUserJwt: intercomConfig.intercom_user_jwt,
              customAttributes,
            }
          : undefined
      }
      shouldInitialize={isIntercomEnabled() && hasConfig}
    >
      {children}
    </ReactIntercomProvider>
  );
};
